import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { PointEvent } from "../../shared/types";
import { timeOfDay } from "../../shared/time";
import { tapFeedback } from "../feedback";
import { signed } from "../format";

/** How long a finger rests on a tap before Delete appears, and how far it may drift meanwhile. */
const HOLD_MS = 500;
const DRIFT_PX = 10;

/**
 * One line of a kid's log: "+5 helped · 17:42 · mum". With `onDelete` (grown-ups, on a tap no
 * closed day holds), pressing and holding the line brings up Delete and Keep, so the list stays
 * clean and a stray touch while scrolling never wipes an entry. Keyboards and screen readers,
 * which cannot hold, reach the same choice through a Delete button that shows only on focus.
 */
export function EventRow({ event, onDelete }: { event: PointEvent; onDelete?: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const detail = [event.reason, timeOfDay(event.at), event.by].filter(Boolean).join(" · ");
  const name = `${signed(event.delta)}${event.reason ? ` ${event.reason}` : ""} at ${timeOfDay(event.at)}`;

  function cancelHold() {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }
  useEffect(() => cancelHold, []);

  function onPointerDown(e: PointerEvent<HTMLLIElement>) {
    if (!onDelete || confirming || e.button !== 0) return;
    start.current = { x: e.clientX, y: e.clientY };
    timer.current = window.setTimeout(() => {
      timer.current = null;
      tapFeedback(25);
      setConfirming(true);
    }, HOLD_MS);
  }

  function onPointerMove(e: PointerEvent<HTMLLIElement>) {
    const s = start.current;
    if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > DRIFT_PX) cancelHold();
  }

  return (
    <li
      className={onDelete ? "event-row event-row--holdable" : "event-row"}
      data-confirming={confirming || undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      // The phone's own long-press menu (copy, look up) would cover the choice.
      onContextMenu={onDelete ? (e) => e.preventDefault() : undefined}
    >
      <span className="event-row__delta">{signed(event.delta)}</span>
      <span className="event-row__detail">{detail}</span>
      {onDelete &&
        (confirming ? (
          <span className="event-row__actions">
            <button
              className="event-row__button event-row__button--danger"
              type="button"
              aria-label={`Delete ${name}`}
              onClick={onDelete}
            >
              Delete
            </button>
            <button className="event-row__button" type="button" autoFocus onClick={() => setConfirming(false)}>
              Keep
            </button>
          </span>
        ) : (
          <button className="event-row__button event-row__key-delete" type="button" onClick={() => setConfirming(true)}>
            Delete<span className="visually-hidden">{` ${name}`}</span>
          </button>
        ))}
    </li>
  );
}
